/**
 * Customer 360 money tabs (Phase 4, source LEDGER): «عملیات مالی» (ledger balances, open admin holds,
 * adjustment history with reversal links, the «اصلاح موجودی» entry point), «تومان» (deposits and
 * withdrawals with internal operating facts) and «دفتر کل» (this customer's journal with postings).
 * Read-only views of server truth; every mutation goes through AdjustmentDialog (step-up, reason,
 * confirmation, idempotency) — there is no other way to change a balance from the console.
 */
import { Badge, Button, DataTable, formatAmount, Ltr, StateView } from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { dt, type Adjustment, type CustomerLedger, type JournalTx } from '../../api.ts';
import {
  ACCOUNT_CLASS,
  ADJ_OP,
  assetName,
  DEPOSIT_METHOD,
  DEPOSIT_STATUS,
  RAIL,
  TX_TYPE,
  unitOf,
  WITHDRAWAL_STATUS,
} from '../../lib/money-labels.ts';
import { TechCode } from '../../lib/ui.tsx';
import { AdjustmentDialog, type AdjustmentTarget } from './AdjustmentDialog.tsx';

const amt = (v: string | null, asset = 'IRT') =>
  v === null ? '—' : <Ltr className="hx-num">{formatAmount(v, unitOf(asset))}</Ltr>;

function Panel({
  title,
  action,
  children,
  ...rest
}: { title: string; action?: ReactNode; children: ReactNode } & Record<`data-${string}`, unknown>) {
  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-line bg-surface-1 p-4 sm:p-5"
      {...rest}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-[14px] font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const reversible = (a: Adjustment) =>
  (a.operation === 'CREDIT' || a.operation === 'DEBIT' || a.operation === 'RESERVE') &&
  a.reversedByAdjustmentId === null &&
  (a.operation !== 'RESERVE' || a.reservationStatus === 'OPEN');

export function MoneyOpsTab({
  userId,
  userLabel,
  l,
  onChanged,
}: {
  userId: string;
  userLabel: string;
  l: CustomerLedger;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState<AdjustmentTarget | null | undefined>(undefined);
  const refOf = (id: string | null) => l.adjustments.find((a) => a.id === id)?.ref ?? null;
  return (
    <div className="flex flex-col gap-4" data-tab-money-ops>
      <Panel
        title="موجودی دفتر کل"
        data-ledger-balances
        action={
          <Button size="sm" onPress={() => setOpen(null)} data-open-adjustment>
            اصلاح موجودی
          </Button>
        }
      >
        <div className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-3">
          {l.balances.map((b) =>
            (
              [
                ['قابل استفاده', b.available, 'available'],
                ['رزروشده', b.reserved, 'reserved'],
                ['کل', b.total, 'total'],
              ] as const
            ).map(([label, v, k]) => (
              <div
                key={`${b.asset}-${k}`}
                className="rounded-md border border-line bg-surface-2 px-3.5 py-3"
                data-balance={`${b.asset}:${k}`}
              >
                <p className="m-0 text-[12px] text-fg-muted">
                  {label} · {assetName(b.asset)}
                </p>
                <p className="m-0 mt-1 text-[17px] font-bold">{amt(v, b.asset)}</p>
              </div>
            )),
          )}
        </div>
        <p className="m-0 text-[12px] leading-6 text-fg-subtle">
          منبع: دفتر کل PostgreSQL. موجودی فقط با سند دوطرفه تغییر می‌کند؛ ویرایش یا حذف سند ممکن
          نیست و اصلاح، سند جبرانی تازه است.
        </p>
      </Panel>

      <Panel title="رزروها و مسدودی‌های باز" data-holds>
        <DataTable
          label="رزروها و مسدودی‌های باز"
          rows={l.holds}
          density="compact"
          columns={[
            {
              id: 'ref',
              header: 'اصلاحیه',
              isRowHeader: true,
              cell: (h) => <Ltr className="font-mono text-[12px]">{h.adjustmentRef ?? '—'}</Ltr>,
            },
            {
              id: 'kind',
              header: 'نوع',
              cell: (h) => (h.kind === 'ADMIN_HOLD' ? 'مسدودی مدیر' : 'رزرو برداشت'),
            },
            { id: 'amount', header: 'مبلغ', numeric: true, cell: (h) => amt(h.amount, h.asset) },
            { id: 'reason', header: 'دلیل', cell: (h) => h.reason ?? '—' },
            { id: 'at', header: 'زمان', cell: (h) => dt(h.createdAt) },
            {
              id: 'act',
              header: 'عملیات',
              cell: (h) =>
                h.kind === 'ADMIN_HOLD' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onPress={() =>
                      setOpen({
                        operation: 'RELEASE',
                        asset: h.asset,
                        amount: h.amount,
                        reservationId: h.id,
                        targetRef: h.adjustmentRef,
                      })
                    }
                    data-release-hold={h.id}
                  >
                    آزادسازی
                  </Button>
                ) : (
                  <span className="text-[12px] text-fg-subtle">با تصمیم برداشت</span>
                ),
            },
          ]}
          empty={<StateView compact kind="empty" title="رزرو یا مسدودی بازی وجود ندارد" />}
        />
      </Panel>

      <Panel title="تاریخچه‌ی اصلاحیه‌ها" data-adjustments>
        <DataTable<Adjustment>
          label="اصلاحیه‌ها"
          rows={l.adjustments}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'ref',
              header: 'کد',
              isRowHeader: true,
              cell: (a) => <Ltr className="font-mono text-[12px]">{a.ref}</Ltr>,
            },
            {
              id: 'op',
              header: 'عملیات',
              cell: (a) => (
                <span className="flex flex-col">
                  <span>{ADJ_OP[a.operation] ?? a.operation}</span>
                  {a.reversesAdjustmentId && (
                    <span className="text-[11px] text-fg-subtle">
                      برگشتِ <Ltr className="font-mono">{refOf(a.reversesAdjustmentId) ?? '—'}</Ltr>
                    </span>
                  )}
                  {a.reversedByAdjustmentId && (
                    <span className="text-[11px] text-negative" data-reversed>
                      برگشت‌خورده با{' '}
                      <Ltr className="font-mono">{refOf(a.reversedByAdjustmentId) ?? '—'}</Ltr>
                    </span>
                  )}
                </span>
              ),
            },
            { id: 'amount', header: 'مبلغ', numeric: true, cell: (a) => amt(a.amount, a.asset) },
            {
              id: 'ba',
              header: 'قابل استفاده: پیش ← پس',
              numeric: true,
              cell: (a) => (
                <Ltr className="hx-num text-[12px]">
                  {formatAmount(a.before.available, unitOf(a.asset), { withUnit: false })} →{' '}
                  {formatAmount(a.after.available, unitOf(a.asset), { withUnit: false })}
                </Ltr>
              ),
            },
            {
              id: 'reason',
              header: 'دلیل / مرجع',
              cell: (a) => (
                <span className="flex flex-col">
                  <span>{a.reason}</span>
                  <TechCode>ref: {a.externalReference}</TechCode>
                  {a.internalNote && (
                    <span className="text-[11px] text-fg-subtle">یادداشت: {a.internalNote}</span>
                  )}
                </span>
              ),
            },
            {
              id: 'admin',
              header: 'مدیر / زمان',
              cell: (a) => (
                <span className="flex flex-col">
                  <Ltr>{a.admin ?? '—'}</Ltr>
                  <span className="text-[11px] text-fg-subtle">{dt(a.createdAt)}</span>
                </span>
              ),
            },
            {
              id: 'tx',
              header: 'سند',
              cell: (a) => (
                <details className="text-[12px]">
                  <summary className="cursor-pointer text-fg-muted">شناسه</summary>
                  <TechCode>{a.transactionId}</TechCode>
                </details>
              ),
            },
            {
              id: 'act',
              header: 'عملیات',
              cell: (a) =>
                reversible(a) ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onPress={() =>
                      setOpen({
                        operation: 'REVERSAL',
                        asset: a.asset,
                        amount: a.amount,
                        reversesAdjustmentId: a.id,
                        targetRef: a.ref,
                      })
                    }
                    data-reverse-adjustment={a.ref}
                  >
                    برگشت
                  </Button>
                ) : null,
            },
          ]}
          empty={<StateView compact kind="empty" title="اصلاحیه‌ای ثبت نشده است" />}
        />
      </Panel>

      {open !== undefined && (
        <AdjustmentDialog
          userId={userId}
          userLabel={userLabel}
          initial={open}
          assets={l.balances.map((b) => b.asset)}
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

export function TomanTab({ l }: { l: CustomerLedger }) {
  return (
    <div className="flex flex-col gap-4" data-tab-toman>
      <Panel
        title="واریزهای تومان"
        action={
          <Link to="/toman-deposits" className="text-[12px] text-accent-text hover:underline">
            صف واریز
          </Link>
        }
      >
        <DataTable
          label="واریزهای تومان"
          rows={l.deposits}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'ref',
              header: 'کد',
              isRowHeader: true,
              cell: (d) => <Ltr className="font-mono text-[12px]">{d.ref}</Ltr>,
            },
            { id: 'm', header: 'روش', cell: (d) => DEPOSIT_METHOD[d.method] ?? d.method },
            {
              id: 'amount',
              header: 'مبلغ اعلامی / نهایی',
              numeric: true,
              cell: (d) => (
                <span className="flex flex-col items-end">
                  {amt(d.declaredAmount)}
                  {d.finalAmount && d.finalAmount !== d.declaredAmount && (
                    <span className="text-[11px] text-fg-subtle">نهایی: {amt(d.finalAmount)}</span>
                  )}
                </span>
              ),
            },
            {
              id: 'src',
              header: 'مبدأ',
              cell: (d) => <Ltr className="font-mono text-[12px]">{d.source.masked}</Ltr>,
            },
            {
              id: 'st',
              header: 'وضعیت',
              cell: (d) => (
                <Badge tone={DEPOSIT_STATUS[d.status]?.tone ?? 'neutral'}>
                  {DEPOSIT_STATUS[d.status]?.label ?? d.status}
                </Badge>
              ),
            },
            { id: 'at', header: 'زمان', cell: (d) => dt(d.createdAt) },
          ]}
          empty={<StateView compact kind="empty" title="واریزی ثبت نشده است" />}
        />
      </Panel>
      <Panel
        title="برداشت‌های تومان"
        action={
          <Link to="/toman-withdrawals" className="text-[12px] text-accent-text hover:underline">
            صف برداشت
          </Link>
        }
      >
        <DataTable
          label="برداشت‌های تومان"
          rows={l.withdrawals}
          density="compact"
          stickyFirst
          columns={[
            {
              id: 'ref',
              header: 'کد',
              isRowHeader: true,
              cell: (w) => <Ltr className="font-mono text-[12px]">{w.ref}</Ltr>,
            },
            { id: 'amount', header: 'مبلغ', numeric: true, cell: (w) => amt(w.amount) },
            {
              id: 'dest',
              header: 'مقصد',
              cell: (w) => (
                <span className="flex flex-col">
                  <Ltr className="font-mono text-[12px]">{w.destination.masked}</Ltr>
                  {w.firstToInstrument && (
                    <span className="text-[11px] text-accent-text">اولین برداشت به این مقصد</span>
                  )}
                </span>
              ),
            },
            {
              id: 'st',
              header: 'وضعیت',
              cell: (w) => (
                <span className="flex flex-col items-start gap-0.5">
                  <Badge tone={WITHDRAWAL_STATUS[w.status]?.tone ?? 'neutral'}>
                    {WITHDRAWAL_STATUS[w.status]?.label ?? w.status}
                  </Badge>
                  {w.rail && <span className="text-[11px] text-fg-subtle">{RAIL[w.rail]}</span>}
                </span>
              ),
            },
            { id: 'at', header: 'زمان', cell: (w) => dt(w.createdAt) },
          ]}
          empty={<StateView compact kind="empty" title="برداشتی ثبت نشده است" />}
        />
      </Panel>
    </div>
  );
}

export function JournalList({ rows, userId }: { rows: JournalTx[]; userId?: string }) {
  if (rows.length === 0) return <StateView compact kind="empty" title="سندی ثبت نشده است" />;
  return (
    <ol className="m-0 flex list-none flex-col gap-2 p-0" data-journal>
      {rows.map((t) => (
        <li key={t.id} className="rounded-md border border-line bg-surface-2 p-3" data-journal-tx>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[13px] font-semibold">
              {TX_TYPE[t.type] ?? 'سند ثبت‌شده'}
              {t.reversesTransactionId && (
                <span className="ms-2 text-[11px] font-normal text-accent-text">سند برگشتی</span>
              )}
              {t.reversedByTransactionId && (
                <span className="ms-2 text-[11px] font-normal text-negative">برگشت خورده</span>
              )}
            </span>
            <span className="text-[12px] text-fg-muted">{dt(t.createdAt)}</span>
          </div>
          <TechCode>
            {t.id} · {t.type} · {t.effect}
          </TechCode>
          <table className="mt-2 w-full border-collapse text-[12px]">
            <tbody>
              {t.postings.map((p, i) => (
                <tr key={i} className="border-t border-line">
                  <td className="py-1.5">
                    {ACCOUNT_CLASS[p.accountClass] ?? p.accountClass}
                    {userId && p.userId && p.userId !== userId && (
                      <span className="ms-1 text-fg-subtle">(کاربر دیگر)</span>
                    )}
                  </td>
                  <td className="py-1.5 text-end">
                    <Ltr
                      className={`hx-num font-semibold ${p.amount.startsWith('-') ? 'text-negative' : 'text-positive'}`}
                    >
                      {p.amount.startsWith('-') ? '' : '+'}
                      {formatAmount(p.amount, unitOf(p.asset), { withUnit: false })}
                    </Ltr>
                  </td>
                  <td className="py-1.5 text-end text-fg-subtle">
                    {p.balanceAfter !== null ? (
                      <>
                        مانده:{' '}
                        <Ltr className="hx-num">
                          {formatAmount(p.balanceAfter, unitOf(p.asset), { withUnit: false })}
                        </Ltr>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </li>
      ))}
    </ol>
  );
}
